import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Dispatch, connect } from 'react-redux';
import { AppActionCreators, IApplicationState, ReportActionCreators } from '@nkr/store/index';
import { ICprScoreResponse } from '@nkr/store/reports';
import { ICprScore } from '@nkr/models/index';
import { GridApi, GridReadyEvent, CsvExportParams, ProcessCellForExportParams, ColDef } from 'ag-grid-community';
import { Button, FormControl } from 'react-bootstrap';
import { IListItem } from '@nkr/models/index';
import Grid from './grid';

interface IProps {
    allCenters: boolean
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getCprScores: (month: number, year: number) => Promise<ICprScoreResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    data: ICprScore[],
    month: number,
    year: number,
    lastMonthEndDate: string,
    lastMonthStartDate: string,
    monthList: { name: string, monthNumber: number }[],
    yearList: IListItem[],
    gridApi: GridApi,
    columns: ColDef[],
}

class CprScoresReport extends React.Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        lastMonthEndDate: '',
        lastMonthStartDate: '',
        month: 0,
        year: 0,
        monthList: [],
        yearList: [],
        gridApi: null,
        columns: []
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api
        });
    }

    componentDidMount = () => {
        let { month, year, monthList, yearList } = this.populateLists();

        this.setState({
            month,
            year,
            monthList,
            yearList
        }, () => {
            this.getData();
        });
    }

    getData = () => {
        let message = 'Getting CPR scores, one moment please...';

        this.props.showLoading(message).then(() => {
            this.props.getCprScores(this.state.month, this.state.year).then(response => {
                let { data, reportEndDate: lastMonthEndDate, reportStartDate: lastMonthStartDate } = response.data.results;

                this.setState({
                    data,
                    lastMonthEndDate,
                    lastMonthStartDate
                }, () => {
                    this.props.hideLoading();
                });
            });
        });
    }

    getKflCenterColumn = (value: number, startDate: string) => {
        let kflstart = moment(startDate);
        let quarterStart = moment(this.state.lastMonthStartDate);
        let quarterEnd = moment(this.state.lastMonthEndDate);

        if (value == -1) {
            return 'N/A';
        }

        if (kflstart >= quarterStart && kflstart < quarterEnd) {
            return 'New';
        }

        return value == 1 ? 'Yes' : 'No';
    }

    populateLists = () => {
        let currentYear = moment().year();
        let currentMonth = moment().month() + 1;
        let allMonths = moment.monthsShort().map((m, i) => ({ name: m, monthNumber: i + 1 }));
        let monthNum = allMonths.findIndex(item => item.monthNumber === currentMonth) + 1;

        let months = allMonths.filter(x => x.monthNumber <= monthNum && x.monthNumber >= monthNum - 3);

        let years = _.range(moment().year(), 2021, -1).map(year => {
            return {
                id: year,
                description: year.toString()
            };
        });

        return { month: currentMonth - 1, year: currentYear, monthList: _.reverse(months), yearList: years };
    }

    handleMonthChange = (e: React.ChangeEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            month: parseInt(e.currentTarget.value)
        });
    };

    handleYearChange = (e: React.ChangeEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            year: parseInt(e.currentTarget.value)
        });
    };

    handleDateRangeSearch = () => {
        this.getData();
    }

    handleExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            allColumns: true,
            fileName: 'center-cpr-scores-using-data-last-12-months.csv',
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.colId == 'cprScore')
                    return _.round((params.value) * 100, 1) + '%';

                if (colDef.colId == 'kflCenter') {
                    return this.getKflCenterColumn(params.value, params.node.data.kflCenterStart);
                }

                if (colDef.colId == 'kflCenterStart') {
                    let newValue = moment(params.value);
                    if (newValue.isValid()) {
                        return newValue.format('MM/DD/YYYY');
                    }
                    else {
                        return '';
                    }
                }

                return params.value;
            }
        };

        this.state.gridApi.exportDataAsCsv(params);
    }

    render = () => {
        return (
            <>
                {this.props.allCenters == false ?
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <div style={{ fontSize: 28, fontWeight: 500 }}>Center Compatible Pair Ratio (CPR) Scores</div>

                    </div>
                    :
                    <div></div>
                }

                <div className='well well-sm' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                            <div style={{ marginRight: 5 }}>Month:&nbsp;</div>
                            <div>
                                <FormControl
                                    componentClass='select'
                                    style={{ width: 'initial' }}
                                    value={this.state.month}
                                    onChange={this.handleMonthChange}
                                >
                                    {this.state.monthList.map(item =>
                                        <option key={'et-' + item.monthNumber} value={item.monthNumber}>{item.name}</option>
                                    )}
                                </FormControl>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20 }}>
                            <div style={{ marginRight: 5 }}>Year:</div>
                            <div>
                                <FormControl
                                    componentClass='select'
                                    style={{ width: 'initial' }}
                                    value={this.state.year}
                                    onChange={this.handleYearChange}
                                >
                                    {this.state.yearList.map((item, index) =>
                                        <option key={'et-' + index} value={item.id}>{item.description}</option>
                                    )}
                                </FormControl>
                            </div>
                        </div>

                        <div style={{ marginLeft: 30 }}>
                            <Button bsStyle='primary' onClick={this.handleDateRangeSearch}>Search</Button>
                        </div>
                    </div>

                    <div>
                        <Button onClick={this.handleExport}>Export CSV</Button>
                    </div>
                </div>

                <Grid data={this.state.data} onGridReady={this.onGridReady} />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>Using data from the last 6 months. Month ending {this.state.lastMonthEndDate}</div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getCprScores: (month, year) => {
            return dispatch(ReportActionCreators.getCprScores(month, year));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CprScoresReport);